import { useCallback, useEffect, useRef, useState } from "react";
import { CreditCard, ImagePlus, Trash2 } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = (s) =>
    s === "betaald"
        ? "border-gold/60 text-gold"
        : s === "verlopen"
          ? "border-red-400/30 text-red-300/70"
          : "border-white/20 text-ash";
const STATUS_LABELS = { concept: "Concept", verstuurd: "Verstuurd", betaald: "Betaald", verlopen: "Verlopen" };

export const MyBilling = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [paying, setPaying] = useState(null);
    const [yearBusy, setYearBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/my-billing");
            setData(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const pay = async (inv) => {
        setPaying(inv.id);
        setError("");
        try {
            const { data } = await platformClient.post(`/billing/invoices/${inv.id}/pay`, {
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
            setPaying(null);
        }
    };

    const startYearDeal = async () => {
        setYearBusy(true);
        setError("");
        try {
            const { data } = await platformClient.post("/billing/year-deal", {
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
            setYearBusy(false);
        }
    };

    if (!data) return null;
    const sub = data.subscription;
    const nowMonth = new Date().toISOString().slice(0, 7);
    const coveredYear = data.invoices.find(
        (i) => i.year_deal && i.status === "betaald" && i.covers_until >= nowMonth
    );
    const yearPrice = sub ? Math.round(sub.monthly_price * 12 * 0.9 * 100) / 100 : 0;

    return (
        <div data-testid="my-billing" className="border border-white/10 bg-coal p-6 md:p-8">
            <p className={LABEL}>
                <CreditCard className="mr-2 inline h-3.5 w-3.5 text-gold" />
                Mijn abonnement
            </p>
            {sub ? (
                <div className="mt-4 flex flex-wrap items-center gap-6">
                    <div>
                        <p className="font-display text-2xl text-bone">{data.plan_label || sub.plan}</p>
                        <p className="mt-1 text-xs text-ash">
                            {euro(sub.monthly_price)}/maand ·{" "}
                            <span className={sub.status === "actief" ? "text-gold" : "text-ash"}>
                                {sub.status === "actief" ? "Actief" : sub.status === "proef" ? "Proefperiode" : sub.status}
                            </span>
                            {sub.start_date ? ` · sinds ${sub.start_date}` : ""}
                        </p>
                    </div>
                    {sub.status === "actief" && sub.monthly_price > 0 ? (
                        coveredYear ? (
                            <p data-testid="year-deal-active" className="border border-gold/40 bg-gold/5 px-4 py-2.5 text-xs text-gold">
                                Jaardeal actief t/m {coveredYear.covers_until} — 10% korting ontvangen
                            </p>
                        ) : (
                            <button
                                data-testid="year-deal-button"
                                onClick={startYearDeal}
                                disabled={yearBusy}
                                className="border border-gold/50 px-4 py-2.5 text-left transition-colors hover:bg-gold/10 disabled:opacity-60"
                            >
                                <span className="block text-[10px] uppercase tracking-[0.2em] text-gold">Jaardeal — 10% korting</span>
                                <span className="mt-0.5 block text-xs text-ash">
                                    12 maanden voor {euro(yearPrice)} i.p.v. {euro(sub.monthly_price * 12)}
                                </span>
                            </button>
                        )
                    ) : null}
                </div>
            ) : (
                <p className="mt-4 text-sm text-ash">Nog geen abonnement actief — neem contact op met Clarke.</p>
            )}

            {data.invoices.length ? (
                <div className="mt-6 space-y-2">
                    {data.invoices.map((inv) => (
                        <div
                            key={inv.id}
                            data-testid={`my-invoice-${inv.id}`}
                            className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-2 text-xs"
                        >
                            <span className="text-bone">
                                <span className="text-gold">{inv.number}</span>
                                {inv.period ? ` · ${inv.period}` : ""} · {inv.issue_date}
                            </span>
                            <span className="flex items-center gap-3">
                                <span className="text-bone">{euro(inv.total)}</span>
                                <span className={`border px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE(inv.status)}`}>
                                    {STATUS_LABELS[inv.status] || inv.status}
                                </span>
                                {inv.status !== "betaald" ? (
                                    <button
                                        data-testid={`pay-invoice-${inv.id}`}
                                        onClick={() => pay(inv)}
                                        disabled={paying === inv.id}
                                        className="flex items-center gap-1.5 bg-gold px-4 py-1.5 text-[10px] uppercase tracking-[0.15em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                                    >
                                        <CreditCard className="h-3 w-3" />
                                        {paying === inv.id ? "Bezig…" : "Betaal nu"}
                                    </button>
                                ) : null}
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="mt-4 text-xs text-ash">Nog geen facturen van Clarke.</p>
            )}
            {error ? (
                <p data-testid="my-billing-error" className="mt-4 text-sm text-red-400">{error}</p>
            ) : null}
        </div>
    );
};

export const BrandingCard = () => {
    const [logoUrl, setLogoUrl] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const fileRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await platformClient.get("/settings/logo");
                setLogoUrl(data.logo_url);
            } catch {}
        })();
    }, []);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await platformClient.post("/settings/logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLogoUrl(`${data.logo_url}?v=${Date.now()}`);
            setNotice("Logo opgeslagen — verschijnt nu op je offertes, facturen en klantportaal");
            setTimeout(() => setNotice(""), 6000);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const remove = async () => {
        if (!window.confirm("Logo verwijderen? Je documenten krijgen weer de standaardkop.")) return;
        try {
            await platformClient.delete("/settings/logo");
            setLogoUrl(null);
        } catch {}
    };

    return (
        <div data-testid="branding-card" className="border border-white/10 bg-coal p-6 md:p-8">
            <p className={LABEL}>
                <ImagePlus className="mr-2 inline h-3.5 w-3.5 text-gold" />
                Huisstijl
            </p>
            <p className="mt-3 text-sm leading-relaxed text-ash">
                Upload je eigen logo — die verschijnt bovenaan je offertes, facturen en in het klantportaal.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
                {logoUrl ? (
                    <div className="flex items-center gap-4 border border-white/10 bg-ink px-4 py-3">
                        <img
                            data-testid="branding-logo-preview"
                            src={`${BACKEND}${logoUrl}`}
                            alt="Bedrijfslogo"
                            className="max-h-10 max-w-40 object-contain"
                        />
                        <button
                            data-testid="branding-logo-remove"
                            onClick={remove}
                            aria-label="Logo verwijderen"
                            className="text-ash/50 transition-colors hover:text-red-400"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <p className="text-xs text-ash/70">Nog geen logo geüpload.</p>
                )}
                <label
                    data-testid="branding-logo-upload"
                    className={`cursor-pointer border border-gold/50 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black ${busy ? "pointer-events-none opacity-60" : ""}`}
                >
                    {busy ? "Uploaden…" : logoUrl ? "Vervang logo" : "Upload logo"}
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={upload}
                        className="hidden"
                        data-testid="branding-logo-input"
                    />
                </label>
            </div>
            <p className="mt-3 text-[11px] text-ash/60">PNG, JPG of WebP — maximaal 2 MB.</p>
            {error ? <p data-testid="branding-error" className="mt-3 text-sm text-red-400">{error}</p> : null}
            {notice ? <p data-testid="branding-notice" className="mt-3 text-xs uppercase tracking-[0.15em] text-gold">{notice}</p> : null}
        </div>
    );
};
